import { Link } from "react-router-dom";
import { ArrowRightIcon as ArrowRight, DatabaseIcon as Database, StackIcon as Stack, LightningIcon as Lightning, FlowArrowIcon as FlowArrow, ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

export default function Framework() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16" data-testid="framework-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
        <div className="md:col-span-8">
          <div className="ml-eyebrow mb-2">Modernization Framework</div>
          <h1 className="text-3xl lg:text-5xl font-semibold tracking-tight">
            How Modernization Lab thinks.
          </h1>
          <p className="mt-5 text-[var(--ml-text-secondary)] max-w-2xl leading-relaxed">
            A reference for the consulting framework, the four-layer architecture, and the three
            investment tiers. Use this as a mental model when chatting with any agent.
          </p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Link to="/hub" className="ml-btn-primary" data-testid="framework-cta-hub">
            Open Agent Hub <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>

      {/* 5-step framework */}
      <section className="mb-16">
        <div className="ml-eyebrow mb-4">Consulting Framework · 5 Steps</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="ml-card p-5 flex flex-col gap-3" data-testid={`step-${i+1}`}>
              <div className="ml-mono text-xs text-[var(--ml-primary)]">STEP 0{i+1}</div>
              <div className="font-medium text-[16px] tracking-tight">{s.title}</div>
              <p className="text-sm text-[var(--ml-text-secondary)] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Architecture layers */}
      <section className="mb-16">
        <div className="ml-eyebrow mb-4">Agentic Modernization · 4 Layers</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {LAYERS.map((l) => (
            <div key={l.title} className="ml-card p-6 flex flex-col gap-3" data-testid={`layer-row-${l.title.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="w-10 h-10 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center">
                <l.Icon size={20} weight="bold" color="#0f62fe" />
              </div>
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                {l.label}
              </div>
              <div className="font-medium text-[17px] tracking-tight">{l.title}</div>
              <p className="text-sm text-[var(--ml-text-secondary)] leading-relaxed">{l.body}</p>
              <ul className="mt-2 space-y-1.5 text-xs text-[var(--ml-text-primary)]">
                {l.bullets.map((b) => (
                  <li key={b} className="flex gap-2 items-start">
                    <CheckCircle size={12} weight="bold" color="#24a148" className="mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Investment tiers */}
      <section className="mb-16">
        <div className="ml-eyebrow mb-4">Investment Options · Always Three</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`ml-card p-6 flex flex-col gap-3 ${t.featured ? "border-[var(--ml-primary)] bg-[var(--ml-primary)]/[0.04]" : ""}`}
              data-testid={`tier-${t.name.toLowerCase()}`}
            >
              <div className="ml-mono text-xs uppercase tracking-widest" style={{ color: t.featured ? "#0f62fe" : "#525252" }}>
                {t.label}
              </div>
              <div className="text-xl font-semibold tracking-tight">{t.name}</div>
              <p className="text-sm text-[var(--ml-text-secondary)]">{t.summary}</p>
              <ul className="mt-2 space-y-2 text-sm">
                {t.points.map((p) => (
                  <li key={p} className="flex gap-2 items-start">
                    <span className="text-[var(--ml-primary)] mt-1">→</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Behavior ledger */}
      <section className="mb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <div className="ml-eyebrow mb-3">Behavior Ledger Concept</div>
            <h2 className="text-2xl lg:text-3xl font-medium tracking-tight">
              Journals are not just audit logs.
              <br />
              They are a behavior ledger.
            </h2>
          </div>
          <div className="md:col-span-7 ml-card p-6">
            <p className="text-[var(--ml-text-primary)] leading-relaxed">
              IBM i journals contain a complete history of business behavior — orders placed, prices
              changed, inventory adjusted, customers updated. Modernization Lab treats journals as
              a strategic asset:
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                "Journaling, event capture, and change tracking by default.",
                "Audit trails for security, compliance, and forensic RCA.",
                "Behavioral analytics for revenue, churn, and operations.",
                "Transformation of legacy transactions into reusable business events.",
              ].map((b) => (
                <li key={b} className="flex gap-2.5">
                  <ShieldCheck size={16} weight="bold" color="#0f62fe" className="mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

const STEPS = [
  { title: "Identify the business problem", body: "Not the symptom. Not the tech. The actual business outcome at risk." },
  { title: "Identify affected processes", body: "Order-to-cash, procure-to-pay, plan-to-produce — map the touch points." },
  { title: "Identify IBM i objects", body: "Files, programs, service programs, queues, APIs, menus — every object that matters." },
  { title: "Identify risks", body: "Tribal knowledge, data integrity, integration coupling, regulatory, reversibility." },
  { title: "Recommend modernization options", body: "Always three: Minimum, Moderate, Strategic. Cost, risk, value compared." },
];

const LAYERS = [
  {
    label: "System of",
    title: "Record",
    body: "IBM i — DB2 for i, journals, RPG service programs.",
    bullets: ["DB2 for i tables", "Journals as event source", "RPG / SQL services"],
    Icon: Database,
  },
  {
    label: "System of",
    title: "Engagement",
    body: "Web, mobile, partner integrations on governed APIs.",
    bullets: ["Web & mobile apps", "Customer / partner portals", "B2B integrations"],
    Icon: Stack,
  },
  {
    label: "System of",
    title: "Intelligence",
    body: "AI agents, analytics, behavioral monitoring.",
    bullets: ["Agentic workflows", "Anomaly detection", "Process intelligence"],
    Icon: Lightning,
  },
  {
    label: "System of",
    title: "Action",
    body: "APIs, workflows, automation — governed actuation.",
    bullets: ["Governed write APIs", "Human-in-the-loop", "Workflow engines"],
    Icon: FlowArrow,
  },
];

const TIERS = [
  {
    label: "Tier 01",
    name: "Minimum Investment",
    summary: "Preserve. Instrument. Add visibility without disturbing production.",
    points: [
      "Turn on journaling broadly",
      "Add observability and logging",
      "Lightweight reporting / BI exposure",
      "Document tribal knowledge",
    ],
  },
  {
    label: "Tier 02",
    name: "Moderate Investment",
    summary: "API-enable. Extract service programs. Modernize selectively.",
    points: [
      "Service program extraction (ILE)",
      "REST / event API enablement",
      "Selective free-format RPG refactor",
      "Integration with modern UX layer",
    ],
    featured: true,
  },
  {
    label: "Tier 03",
    name: "Strategic Investment",
    summary: "Agentic, event-driven, transformative — without ERP replacement.",
    points: [
      "Event-driven core via journal CDC",
      "AI agents with governed boundaries",
      "New systems of engagement",
      "Behavior-ledger-driven analytics",
    ],
  },
];
